/**
 * Logica pura di PRESENTAZIONE del modulo Allenamenti (ADR 029): formati delle
 * righe compatte densità/Mallo e note testuali dei pannelli. Le FORMULE vivono
 * in @leopart/calc — qui si formatta soltanto, e da un punto solo, così la
 * card della settimana e la tabella dell'analisi non possono divergere.
 */
import { densitaDi, FLOWS } from '@leopart/calc';
import {
  ITrainingBlock,
  ITrainingMixMalloIdc,
} from '../../models/allenamenti';

/** Abbreviazioni del flusso per gli spazi stretti (tabella). */
export const FLOW_SHORT: Record<string, string> = {
  continuo: 'Cont.',
  intermittente: 'Interm.',
  analitico: 'Anal.',
};

export const flowLabel = (flowType: string | null | undefined): string | null =>
  flowType ? (FLOWS.find((f) => f.id === flowType)?.labelIt ?? flowType) : null;

/**
 * «30×40 m · 10 gioc. · 120 m²/g · eff. 11'» (breve: «30×40 · 10g · 120 m²/g»).
 * `null` quando i dati campo/giocatori non bastano a derivare la densità.
 */
export function rigaDensita(
  block: Pick<
    ITrainingBlock,
    'fieldWidthM' | 'fieldLengthM' | 'playersOnField' | 'effectiveMinutes'
  >,
  breve = false,
): string | null {
  const d = densitaDi(block.fieldWidthM, block.fieldLengthM, block.playersOnField);
  if (d == null) return null;
  const giocatori = breve ? `${block.playersOnField}g` : `${block.playersOnField} gioc.`;
  const campo = breve
    ? `${block.fieldWidthM}×${block.fieldLengthM}`
    : `${block.fieldWidthM}×${block.fieldLengthM} m`;
  const effettivo =
    block.effectiveMinutes != null ? ` · eff. ${block.effectiveMinutes}'` : '';
  return `${campo} · ${giocatori} · ${Math.round(d)} m²/g${effettivo}`;
}

/**
 * «Mallo 80% · Continuo» (breve: «80% · Cont.», senza prefisso).
 * `null` quando il blocco non ha né peso né flusso.
 */
export function rigaMallo(
  block: Pick<ITrainingBlock, 'malloWeight' | 'flowType'>,
  breve = false,
): string | null {
  if (block.malloWeight == null && !block.flowType) return null;
  const peso = block.malloWeight != null ? `${block.malloWeight}%` : 'n/d';
  const flusso = block.flowType
    ? ` · ${breve ? (FLOW_SHORT[block.flowType] ?? block.flowType) : flowLabel(block.flowType)}`
    : '';
  return breve ? `${peso}${flusso}` : `Mallo ${peso}${flusso}`;
}

/** La nota sotto la stat Continuo/Intermittente del pannello Mallo. */
export function notaRapportoCI(
  mallo: Pick<ITrainingMixMalloIdc, 'ciRatio' | 'ciAlert' | 'byFlow'>,
): string {
  if (mallo.ciRatio != null) {
    return mallo.ciAlert
      ? 'sotto 1.0 — spostato sull’intermittente'
      : 'equilibrato / continuo';
  }
  const minuti = (flow: string) =>
    mallo.byFlow.find((f) => f.flow === flow)?.minutes ?? 0;
  if (minuti('continuo') > 0) return 'solo lavoro continuo';
  if (minuti('analitico') > 0) return 'solo lavoro analitico';
  return 'flusso non classificato';
}
